import { Icon } from './Icon';
import type { MessageSummary } from '../types';
import { attachmentKind, avatarHue, fileSize, fmtCount, initials, listDate } from '../lib/format';

interface Props {
  messages: MessageSummary[];
  selectedUid: number | null;
  checked: Set<number>;
  onOpen: (uid: number) => void;
  onCheck: (uid: number, checked: boolean) => void;
  onToggleAll: () => void;
  onToggleFlag: (uid: number, flagged: boolean) => void;
  /** Sətir üzərinə gələndə görünən sürətli əməliyyatlar. */
  onQuick: (uid: number, action: 'archive' | 'trash' | 'seen') => void;
  /** Yalnız oxunmamışları göstər. */
  unreadOnly: boolean;
  onToggleUnread: () => void;
  /**
   * Göndərilənlər/Qaralamalarda sətirdə ALICI göstərilsin.
   *
   * ⚠️ BUNSUZ SİYAHI MƏNASIZDIR. Göndərilənlər qovluğunda hər sətirdə
   * ÖZ adın yazılırdı — 50 sətrin hamısında eyni ad. Halbuki orada
   * lazım olan yeganə məlumat KİMƏ göndərdiyindir. Gmail, Yandex,
   * Outlook — hamısı bu qovluqlarda alıcını göstərir.
   */
  showRecipient: boolean;
  /** Qovluqdakı ümumi say — yüklənmiş sayla müqayisə üçün. */
  total: number;
  loadingMore: boolean;
  onLoadMore: () => void;
  loading: boolean;
  /** Siyahı ümumiyyətlə yüklənmədi — boş sahə yerinə izah göstərilir. */
  hasError: boolean;
  onRetry: () => void;
}

export function MessageList({
  messages,
  selectedUid,
  checked,
  onOpen,
  onCheck,
  onToggleAll,
  onToggleFlag,
  onQuick,
  unreadOnly,
  onToggleUnread,
  showRecipient,
  total,
  loadingMore,
  onLoadMore,
  loading,
  hasError,
  onRetry,
}: Props): React.ReactElement {
  if (loading) {
    /*
      SKELET — FIRLANAN ÇARX DEYİL.
      ⚠️ Böyük boş sahədə tək spinner interfeysi YAVAŞ göstərir: göz
      heç nə tutmur və gözləmə uzun hiss olunur. Skelet gələcək
      məzmunun formasını əvvəlcədən çəkir — istifadəçi «nə gələcəyini»
      bilir və eyni müddət qısa hiss olunur.

      ⚠️ `aria-busy` + `aria-label` MƏCBURİDİR: ekran oxuyucu üçün
      skelet sətirləri MƏNASIZDIR, ona görə onlar `aria-hidden`-dir və
      vəziyyət bir cümlə ilə elan olunur.
    */
    return (
      <ul className="messages" aria-busy="true" aria-label="Məktublar yüklənir">
        {Array.from({ length: 8 }, (_, i) => (
          <li className="msg is-skeleton" key={i} aria-hidden="true">
            <span className="sk sk-check" />
            <span className="sk sk-star" />
            <span className="sk sk-avatar" />
            <span className="sk sk-from" />
            {/* Enlər dəyişir — eyni enli sətirlər «cədvəl» kimi görünür,
                real poçt siyahısı isə qeyri-bərabərdir. */}
            <span className="sk sk-text" style={{ width: `${45 + ((i * 13) % 40)}%` }} />
            <span className="sk sk-date" />
          </li>
        ))}
      </ul>
    );
  }

  /*
    ⚠️ XƏTA BOŞLUQDAN ƏVVƏL YOXLANILIR.
    Sıra tərsinə olsa, yüklənmə sınanda istifadəçi «Bu qovluq boşdur»
    görürdü — yəni sistem ona YALAN deyirdi. Xəta ilə boşluq iki fərqli
    haldır və fərqli cümlə tələb edir.
  */
  if (hasError) {
    return (
      <div className="list-state" role="alert">
        <p className="empty-title">Məktublar yüklənmədi</p>
        <p className="empty-sub">
          Bağlantı kəsilmiş və ya server müvəqqəti cavab verməmiş ola bilər.
          Məktublarınız yerindədir.
        </p>
        <button className="btn-secondary" onClick={onRetry}>
          Yenidən cəhd et
        </button>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="list-state">
        <p className="empty-title">Bu qovluq boşdur</p>
        <p className="empty-sub">Yeni məktub gələndə burada görünəcək.</p>
      </div>
    );
  }

  const allChecked = checked.size > 0 && checked.size === messages.length;

  return (
    <ul className="messages">
      {/* Hamısını seçmə — 50 məktubu bir-bir işarələmək əziyyətdir. */}
      <li className="msg-head">
        <input
          type="checkbox"
          className="msg-check"
          checked={allChecked}
          ref={(el) => {
            // Qismən seçim üçün üçüncü vəziyyət — nə boş, nə dolu.
            if (el) el.indeterminate = checked.size > 0 && !allChecked;
          }}
          onChange={onToggleAll}
          aria-label="Hamısını seç"
        />
        {/* ⚠️ SAY DÜRÜST OLMALIDIR. «11 məktub» yazıb 300-dən 11-ini
            göstərmək istifadəçini çaşdırır — «X / Y» formatı vəziyyəti
            olduğu kimi deyir. */}
        {/* ⚠️ SÜZGƏC BAŞLIQDADIR, ayrıca panel deyil. Poçt siyahısında ən
            tez-tez lazım olan süzgəc «yalnız oxunmamışlar»dır; onu ayrı
            menyuya gizlətmək gündəlik hərəkəti iki klik edir. */}
        <button
          type="button"
          className={`msg-filter${unreadOnly ? ' is-on' : ''}`}
          onClick={onToggleUnread}
          aria-pressed={unreadOnly}
        >
          Oxunmamış
        </button>
        <span className="msg-head-count">
          {total > messages.length
            ? `${fmtCount(messages.length)} / ${fmtCount(total)} məktub`
            : `${fmtCount(messages.length)} məktub`}
          {checked.size > 0 ? ` · ${checked.size} seçildi` : ''}
        </span>
      </li>
      {messages.map((m) => (
        <Row
          key={m.uid}
          message={m}
          active={m.uid === selectedUid}
          checked={checked.has(m.uid)}
          onOpen={onOpen}
          onCheck={onCheck}
          onToggleFlag={onToggleFlag}
          onQuick={onQuick}
          showRecipient={showRecipient}
        />
      ))}

      {/* Daha çox yükləmə — sonsuz sürüşdürmə YOX, açıq düymə.
          Sonsuz sürüşdürmə uzun siyahılarda istifadəçini ilişdirir və
          «sona çatdım» hissi vermir. Düymə vəziyyəti dəqiq göstərir. */}
      {messages.length < total && (
        <li className="msg-more">
          <button className="btn-text" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore
              ? 'Yüklənir…'
              : `Daha ${Math.min(50, total - messages.length)} məktub yüklə`}
          </button>
          <span className="msg-more-hint">
            {messages.length} / {total}
          </span>
        </li>
      )}
    </ul>
  );
}

function Row({
  message: m,
  active,
  checked,
  onOpen,
  onCheck,
  onToggleFlag,
  onQuick,
  showRecipient,
}: {
  message: MessageSummary;
  active: boolean;
  checked: boolean;
  onOpen: (uid: number) => void;
  onCheck: (uid: number, checked: boolean) => void;
  onToggleFlag: (uid: number, flagged: boolean) => void;
  onQuick: (uid: number, action: 'archive' | 'trash' | 'seen') => void;
  showRecipient: boolean;
}): React.ReactElement {
  // ⚠️ AVATAR VƏ BAŞ HƏRFLƏR DƏ GÖSTƏRİLƏN ŞƏXSDƏN GƏLİR.
  // Yalnız adı dəyişsək, Göndərilənlərdə hər sətirdə alıcının adı yanında
  // ÖZ avatarımız dururdu — daha da çaşdırıcı olardı.
  const sender = m.from ?? { name: '', address: '(naməlum)' };
  const first = m.to[0];
  const who = showRecipient && first ? first : sender;
  const extra = showRecipient ? Math.max(0, m.to.length - 1) : 0;
  const from = who;
  const hue = avatarHue(from.address);

  return (
    <li
      className={`msg${m.seen ? '' : ' is-unread'}${checked ? ' is-checked' : ''}${
        active ? ' is-active' : ''
      }`}
    >
      <input
        type="checkbox"
        className="msg-check"
        checked={checked}
        onChange={(e) => onCheck(m.uid, e.target.checked)}
        aria-label={`${m.subject} — seç`}
        onClick={(e) => e.stopPropagation()}
      />

      {/*
        ULDUZ — bütün poçt xidmətlərində var və eyni yerdə durur.
        ⚠️ `stopPropagation` vacibdir: ulduz sətrin içindədir və onsuz
        klikləmək məktubu açardı.
      */}
      <button
        className={`msg-star ${m.flagged ? 'is-on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFlag(m.uid, !m.flagged);
        }}
        aria-pressed={m.flagged}
        aria-label={m.flagged ? 'Ulduzu götür' : 'Ulduz qoy'}
        title={m.flagged ? 'Ulduzu götür' : 'Ulduz qoy'}
      >
        <Icon name={m.flagged ? 'star-filled' : 'star'} size={17} />
      </button>

      <span
        className="msg-avatar"
        style={{ background: `hsl(${hue} var(--avatar-s) var(--avatar-l))` }}
        aria-hidden="true"
      >
        {initials(from.name, from.address)}
      </span>

      {/* Bütün sətir klikləndikdə açılır — hədəf sahəsi böyük olsun. */}
      <button className="msg-main" onClick={() => onOpen(m.uid)}>
        <span className="msg-from">
          {from.name || from.address}
          {/* Çox alıcılı məktubda «+2» — kimə getdiyi tam görünməsə də sayı bilinir. */}
          {extra > 0 && <span className="msg-from-more"> +{extra}</span>}
        </span>

        <span className="msg-body">
          {/*
            CAVAB VERİLMİŞ / YÖNLƏNDİRİLMİŞ GÖSTƏRİCİSİ.
            ⚠️ `answered` bayrağı API-dən ÇOXDAN GƏLİRDİ, sadəcə heç yerdə
            göstərilmirdi. Gmail və Yandex hər ikisi mövzudan əvvəl geriyə
            ox qoyur. Faydası: siyahıya baxan adam hansı məktuba artıq
            cavab verdiyini görür — açıb yoxlamağa ehtiyac qalmır.
          */}
          {m.answered && (
            <span className="msg-answered" title="Cavab verilib" aria-label="Cavab verilib">
              <Icon name="reply" size={13} />
            </span>
          )}
          {/*
            ⚠️ MÖVZU VƏ ÖNİZLƏMƏ BİR KƏSİLMƏ QUTUSUNDADIR.
            Əvvəl hər ikisinin öz `overflow: hidden` + `text-overflow`
            qaydası vardı, yəni sətirdə İKİ «…» çıxırdı:
              «Sifariş təsdiqi haqqın… — hörmətli müştəri…»
            Üstəlik mövzuya `max-width: 55%` qoyulmuşdu — önizləmə qısa
            olanda belə mövzu kəsilirdi və sağda boş yer qalırdı.
            İndi kəsilmə YALNIZ bu sarğıdadır: mövzuya sətrin qalan
            hissəsi bütöv çatır və kəsilmə nöqtəsi bir dənədir.
          */}
          <span className="msg-text">
            <span className="msg-subject">
              {m.subject || <span className="msg-nosubject">(mövzusuz)</span>}
            </span>
            {m.preview && <span className="msg-preview">{m.preview}</span>}
          </span>
        </span>

        {/*
          ⚠️ TARİX SON ELEMENTDİR — QƏSDƏN.
          Geniş ekranda sətir tək sıradır: göndərən | mövzu+önizləmə | tarix.
          Tarixi əvvələ qoysaq, sağ kənarda hizalana bilməzdi. Dar ekranda
          isə CSS `order` ilə göndərənin yanına qaldırılır.
        */}
        <span className="msg-date">{listDate(m.date)}</span>

        {/*
          QOŞMA ÇİPLƏRİ — FAYL ADI İLƏ, SƏTRİN ALTINDA.
          ⚠️ ƏVVƏLKİ ŞƏRH SƏHV İDİ. Burada yazılmışdı ki «heç bir böyük poçt
          xidməti siyahıda fayl adı göstərmir» — Gmail məhz göstərir:
          sətrin altında yuvarlaq çiplər, ikon + qısaldılmış fayl adı
          («📄 invoice-ps_340…»). Yanlış müşahidəyə görə real faydalı
          funksiya silinmişdi.
          Niyə faydalıdır: «hesab-faktura hansı məktubda idi?» sualına
          siyahını açmadan cavab verir — sancaq bunu edə bilmir.

          ⚠️ SƏTRİN İÇİNDƏ DEYİL, ALTINDA. Əvvəlki narahatlıq düzgün idi:
          çipləri əsas sətrə yığmaq önizləməyə yer qoymur. Gmail-in həlli
          onları AYRI sətrə salmaqdır — həm ad görünür, həm önizləmə itmir.

          ⚠️ ÜÇDƏN ÇOXU YIĞILIR. 12 qoşmalı məktub bütün siyahını dağıdardı.
        */}
        {m.attachments.length > 0 && (
          <span className="msg-atts" aria-label={`${m.attachments.length} qoşma`}>
            {m.attachments.slice(0, 3).map((a, i) => (
              <span
                key={i}
                className="msg-att"
                title={`${a.filename} · ${fileSize(a.size)} · ${attachmentKind(a.type, a.filename)}`}
              >
                <Icon name="paperclip" size={12} />
                <span className="msg-att-name">{a.filename}</span>
              </span>
            ))}
            {m.attachments.length > 3 && (
              <span className="msg-att is-more">+{m.attachments.length - 3}</span>
            )}
          </span>
        )}
      </button>

    {/*
      SÜRƏTLİ ƏMƏLİYYATLAR — yalnız kursor sətrin üzərində olanda.

      ⚠️ `.msg-main` DÜYMƏSİNDƏN KƏNARDADIR — şərh əvvəl də belə deyirdi,
      amma KOD ONU ETMİRDİ: blok düymənin içində qalmışdı, yəni
      düymə-içində-düymə pozuntusu real idi. HTML-də bu etibarsızdır;
      brauzer daxili düyməni öz-özünə çıxarır, klik hadisələri qarışır və
      klaviatura ilə gəzinti pozulur.

      İndi `<li>`-nin birbaşa övladıdır və mütləq mövqe ilə sətrin sağında
      durur (`.msg` position:relative).
    */}
    <span className="msg-quick" onClick={(e) => e.stopPropagation()}>
      <button
        className="msg-quick-btn"
        title={m.seen ? 'Oxunmamış işarələ' : 'Oxunmuş işarələ'}
        aria-label={m.seen ? 'Oxunmamış işarələ' : 'Oxunmuş işarələ'}
        onClick={(e) => {
          e.stopPropagation();
          onQuick(m.uid, 'seen');
        }}
      >
        <Icon name={m.seen ? 'draft' : 'inbox'} size={16} />
      </button>
      <button
        className="msg-quick-btn"
        title="Arxivə"
        aria-label="Arxivə"
        onClick={(e) => {
          e.stopPropagation();
          onQuick(m.uid, 'archive');
        }}
      >
        <Icon name="archive" size={16} />
      </button>
      <button
        className="msg-quick-btn is-danger"
        title="Sil"
        aria-label="Sil"
        onClick={(e) => {
          e.stopPropagation();
          onQuick(m.uid, 'trash');
        }}
      >
        <Icon name="trash" size={16} />
      </button>
    </span>

    </li>
  );
}
